/**
 * Bundled release notes loader.
 *
 * Release content lives in `frontend/src/content/releases/v*.md`. Each version
 * can optionally have a sibling `v*.psc.md` whose body is appended for non-FP
 * roles (EVO_ADMIN / EVO_TEAM / PSC_TEAM) and rendered in the "PSC /
 * Operations updates" panel.
 *
 * A version may also carry a `v*.dev.md` developer changelog. It ships in the
 * bundle (the glob is broad) but is NEVER rendered in-app - `buildReleases`
 * skips it explicitly, the same way it skips `.psc.md` siblings. It's an
 * internal-only file kept beside the others for convenience.
 *
 * Frontmatter is parsed inline (small YAML subset) to avoid pulling in
 * gray-matter just for three fields.
 */

import type { User } from '../services/userApi'

export interface Release {
  version: string
  date: string
  title: string
  body: string
  internalBody?: string
}

type RawModule = string

const FP_MODULES = import.meta.glob<RawModule>(
  '../content/releases/v*.md',
  { query: '?raw', import: 'default', eager: true },
) as Record<string, RawModule>

const PSC_MODULES = import.meta.glob<RawModule>(
  '../content/releases/v*.psc.md',
  { query: '?raw', import: 'default', eager: true },
) as Record<string, RawModule>

const FRONTMATTER_RE = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?/

interface ParsedFile {
  meta: Record<string, string>
  body: string
}

function parseFile(raw: string): ParsedFile {
  const match = raw.match(FRONTMATTER_RE)
  if (!match) {
    return { meta: {}, body: raw.trim() }
  }
  const meta: Record<string, string> = {}
  for (const line of match[1].split(/\r?\n/)) {
    const idx = line.indexOf(':')
    if (idx === -1) continue
    const key = line.slice(0, idx).trim()
    let value = line.slice(idx + 1).trim()
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1)
    }
    if (key) meta[key] = value
  }
  return { meta, body: raw.slice(match[0].length).trim() }
}

function versionFromPath(path: string): string | null {
  const match = path.match(/v([\d.]+)(?:\.psc)?\.md$/)
  return match ? match[1] : null
}

export function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

function canSeePsc(role?: User['role']): boolean {
  return role === 'EVO_ADMIN' || role === 'EVO_TEAM' || role === 'PSC_TEAM'
}

/**
 * Pure, test-friendly merge of raw module maps into Release records.
 * `getReleases` wraps this around the Vite-glob results.
 */
export function buildReleases(
  fpModules: Record<string, string>,
  pscModules: Record<string, string>,
  includePsc: boolean,
): Release[] {
  const pscByVersion = new Map<string, string>()
  for (const [path, raw] of Object.entries(pscModules)) {
    const version = versionFromPath(path)
    if (!version) continue
    const parsed = parseFile(raw)
    pscByVersion.set(version, parsed.body)
  }

  const releases: Release[] = []
  for (const [path, raw] of Object.entries(fpModules)) {
    if (path.endsWith('.psc.md')) continue
    if (path.endsWith('.dev.md')) continue
    const version = versionFromPath(path)
    if (!version) continue
    const parsed = parseFile(raw)
    const meta = parsed.meta
    // Hide releases with no FP body from FP-only viewers; PSC-only content
    // would otherwise render as an empty card for them.
    if (!includePsc && !parsed.body.trim()) continue
    releases.push({
      version: meta.version || version,
      date: meta.date || '',
      title: meta.title || `Version ${version}`,
      body: parsed.body,
      internalBody: includePsc ? pscByVersion.get(version) : undefined,
    })
  }

  releases.sort((a, b) => compareSemver(b.version, a.version))
  return releases
}

export function getReleases(role?: User['role']): Release[] {
  return buildReleases(FP_MODULES, PSC_MODULES, canSeePsc(role))
}

export function hasUnseen(
  currentVersion: string | null | undefined,
  lastSeenVersion: string | null | undefined,
): boolean {
  if (!currentVersion) return false
  if (!lastSeenVersion) return true
  return compareSemver(currentVersion, lastSeenVersion) > 0
}

/**
 * First paragraph of the body, before any `##` heading. Used as the one-line
 * preview in collapsed release cards. Returns an empty string when the body
 * jumps straight into a heading.
 */
export function extractSummary(body: string): string {
  if (!body) return ''
  const trimmed = body.trimStart()
  const headingIdx = trimmed.search(/^##\s/m)
  const lead = headingIdx === -1 ? trimmed : trimmed.slice(0, headingIdx)
  const firstPara = lead.split(/\n\s*\n/)[0] ?? ''
  return firstPara.replace(/\s+/g, ' ').trim()
}

/**
 * Body with the lead paragraph removed. Used when the lead is already being
 * surfaced by `extractSummary` in the card header, so it doesn't render twice
 * inside the expanded body.
 */
export function stripLeadingParagraph(body: string): string {
  if (!body) return ''
  const trimmed = body.trimStart()
  const headingIdx = trimmed.search(/^##\s/m)
  const lead = headingIdx === -1 ? trimmed : trimmed.slice(0, headingIdx)
  if (!lead.trim()) return trimmed
  const paraSplit = lead.search(/\n\s*\n/)
  const firstParaEnd = paraSplit === -1 ? lead.length : paraSplit
  return trimmed.slice(firstParaEnd).trimStart()
}

/**
 * Count of top-level `- ` bullets in the body. Drives the "X items" suffix on
 * collapsed PSC-notes panels.
 */
export function countBullets(body: string): number {
  if (!body) return 0
  const matches = body.match(/^\s*-\s+\S/gm)
  return matches ? matches.length : 0
}

export interface MarkdownSection {
  /** Text after the `### ` marker; empty string for pre-heading preamble. */
  heading: string
  /** Markdown for that subsection, heading line included. */
  content: string
}

/**
 * Splits a markdown body into its `###` (h3) subsections. Returns `[]` when the
 * body has no `###` heading, so callers can fall back to single-flow rendering.
 *
 * Content before the first `###` (e.g. a `##` heading + intro) is returned as a
 * leading section with an empty `heading`, so a caller laying sections out in a
 * grid can span it full-width above the columns.
 */
export function splitMarkdownSections(body: string): MarkdownSection[] {
  if (!body || !/^###\s/m.test(body)) return []
  const parts = body.split(/^(?=###\s)/m)
  const sections: MarkdownSection[] = []
  for (const part of parts) {
    const content = part.trim()
    if (!content) continue
    const match = content.match(/^###\s+(.*)$/m)
    sections.push({ heading: match ? match[1].trim() : '', content })
  }
  return sections
}
